import {
  Badge,
  Button,
  Comments,
  DataTable,
  Field,
  Loading,
  Modal,
  Options,
  StagePath,
  type PathOutcome,
  errorMessage,
  formatDate,
  useToast,
  type BadgeKind,
} from "@corecrm/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api, fileUrl } from "../api/client";
import {
  keys,
  useFeatureRequest,
  useFeatureRequestFiles,
  useFeatureRequestOriginals,
  useFeatureRequestStories,
  useFeatureRequests,
  useRequestFeature,
} from "../api/queries";
import type { FeatureRequest, FeatureRequestStatus, Project, TaskPriority } from "../api/types";
import { RecordUnavailable, closeRecord } from "./bits";
import { fileSize } from "../lib/labels";

/* Feature requests in the portal (UX-196): the project's Feature requests tab, asking for a feature, and a request's detail with its estimate, where it was planned,
   its files and comments. Internal requests never show here. */

export const PRIORITIES: TaskPriority[] = ["urgent", "high", "medium", "low"];
export const PRIORITY_LABEL: Record<TaskPriority, string> = {
  urgent: "Urgent",
  high: "High",
  medium: "Medium",
  low: "Low",
};
const PRIORITY_KIND: Record<TaskPriority, BadgeKind> = {
  urgent: "bad",
  high: "high",
  medium: "info",
  low: "mute",
};
const STATUS_KIND: Record<FeatureRequestStatus, BadgeKind> = {
  new: "info",
  under_review: "acc",
  accepted: "warn",
  planned: "high",
  converted: "ok",
  closed: "mute",
};

/** "6.5 hours", or null when not estimated yet. */
export function hoursText(hours: string | null): string | null {
  if (hours === null || hours === "") return null;
  const n = Number(hours);
  if (!Number.isFinite(n)) return null;
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })} hour${n === 1 ? "" : "s"}`;
}

export function FeatureRequestsView({ project }: { project: Project }) {
  const [open, setOpen] = useState(true);
  // The open request is in the address (/projects/:id/feature-requests/:requestId).
  const { requestId: viewing } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const setViewing = (id: string | null) =>
    id
      ? navigate(`/projects/${project.id}/feature-requests/${id}`, { state: { fromList: true } })
      : closeRecord(navigate, location.state, `/projects/${project.id}?view=feature-requests`);
  const [requesting, setRequesting] = useState(false);
  const list = useFeatureRequests(project.id, open);
  return (
    <div className="stack">
      <div className="row between">
        <label className="check">
          <input type="checkbox" checked={open} onChange={(e) => setOpen(e.target.checked)} />
          <span>Open only</span>
        </label>
        {project.can_log_feature_requests && (
          <Button variant="primary" onClick={() => setRequesting(true)}>
            Request a feature
          </Button>
        )}
      </div>
      {list.isPending ? (
        <Loading />
      ) : (
        <DataTable
          label="Feature requests"
          rows={list.data ?? []}
          rowKey={(r) => r.id}
          empty="No feature requests yet."
          columns={[
            {
              header: "Request",
              cell: (r) => (
                <button type="button" className="task-title" onClick={() => setViewing(r.id)}>
                  <span className="mono faint">{r.key}</span> <strong>{r.title}</strong>
                </button>
              ),
            },
            {
              header: "Priority",
              cell: (r) => <Badge kind={PRIORITY_KIND[r.priority]}>{PRIORITY_LABEL[r.priority]}</Badge>,
            },
            { header: "Status", cell: (r) => <Badge kind={STATUS_KIND[r.status]}>{r.status_label}</Badge> },
            { header: "Estimate", cell: (r) => hoursText(r.estimate_hours) ?? "—" },
            { header: "Story", cell: (r) => (r.story ? r.story.title : "—") },
            { header: "Requested", cell: (r) => formatDate(r.created_at) },
          ]}
        />
      )}
      {requesting && <RequestFeatureDialog project={project} onClose={() => setRequesting(false)} />}
      {viewing && (
        <FeatureRequestDialog project={project} requestId={viewing} onClose={() => setViewing(null)} />
      )}
    </div>
  );
}

/** Ask for a feature (the Story field links it to a story). */
export function RequestFeatureDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const toast = useToast();
  const maintenance = project.billing_model === "maintenance";
  const originals = useFeatureRequestOriginals(project.id, maintenance);
  const [forProject, setForProject] = useState<string | null>(null);
  const stories = useFeatureRequestStories(project.id, forProject);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [value, setValue] = useState("");
  const [priority, setPriority] = useState<TaskPriority>("medium");
  const [storyId, setStoryId] = useState("");
  const send = useRequestFeature(project.id);
  const submit = () =>
    send.mutate(
      {
        title: title.trim(),
        description: description.trim() || null,
        business_value: value.trim() || null,
        priority,
        story_id: storyId || null,
        ...(forProject ? { for_project_id: forProject } : {}),
      },
      {
        onSuccess: (r) => {
          toast(`${r.key} sent. The team will review it.`);
          onClose();
        },
        onError: (e) => toast(errorMessage(e), "err"),
      },
    );
  return (
    <Modal
      wide
      title="Request a feature"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={send.isPending} disabled={!title.trim()} onClick={submit}>
            Send request
          </Button>
        </>
      }
    >
      <div className="form">
        <Field label="What you'd like" full>
          <input type="text" value={title} maxLength={300} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="How important is it">
          <select value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
            <Options list={PRIORITIES.map((p) => [p, PRIORITY_LABEL[p]] as [string, string])} />
          </select>
        </Field>
        {maintenance && (originals.data ?? []).length > 0 && (
          <Field label="Which project">
            <select
              value={forProject ?? ""}
              onChange={(e) => {
                setForProject(e.target.value || null);
                setStoryId("");
              }}
            >
              <option value="">This one</option>
              <Options list={(originals.data ?? []).map((o) => [o.id, o.name] as [string, string])} />
            </select>
          </Field>
        )}
        <Field label="Story" hint="The feature it builds on (optional)">
          <select value={storyId} onChange={(e) => setStoryId(e.target.value)}>
            <option value="">None</option>
            <Options list={(stories.data ?? []).map((s) => [s.id, s.title] as [string, string])} />
          </select>
        </Field>
        <Field label="Describe it" full>
          <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Why it matters" hint="What it would let you do, or save you" full>
          <textarea rows={2} value={value} onChange={(e) => setValue(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function Block({ title, text }: { title: string; text: string | null }) {
  if (!text) return null;
  return (
    <div>
      <strong>{title}</strong>
      <p className="task-desc">{text}</p>
    </div>
  );
}

/** One request: what was asked, where it stands (estimate, the quote it's planned in, the story
    it became), its files and comments. */
const FR_LABEL: Record<FeatureRequestStatus, string> = {
  new: "New",
  under_review: "Under review",
  accepted: "Accepted",
  planned: "Planned",
  converted: "Converted",
  closed: "Closed",
};
const FR_STEPS = (["new", "under_review", "accepted", "planned"] as FeatureRequestStatus[]).map((key) => ({
  key,
  label: FR_LABEL[key],
}));

/** The staff team's status arrows, read-only: Converted (it became a story) ends the path; Closed
    (declined, duplicate or completed) shows only when that is where the request is. */
export function RequestStatusPath({ request }: { request: FeatureRequest }) {
  const outcomes: PathOutcome[] = [{ key: "converted", label: FR_LABEL.converted, tone: "won" }];
  if (request.status === "closed") outcomes.push({ key: "closed", label: FR_LABEL.closed, tone: "lost" });
  return (
    <StagePath
      readOnly
      label="Feature request status"
      steps={FR_STEPS}
      outcomes={outcomes}
      current={request.status}
    />
  );
}

export function FeatureRequestDialog({
  project,
  requestId,
  onClose,
}: {
  project: Project;
  requestId: string;
  onClose: () => void;
}) {
  const request = useFeatureRequest(project.id, requestId);
  const qc = useQueryClient();
  // A request of another project is as unavailable as a missing one.
  const r = request.data && request.data.project_id === project.id ? request.data : undefined;
  if (request.error || (request.data && !r))
    return <RecordUnavailable what="feature request" error={request.error} onClose={onClose} />;
  return (
    <Modal
      wide
      className="record"
      title={r ? `${r.key} · ${r.title}` : "Feature request"}
      onClose={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {!r ? (
        <Loading />
      ) : (
        <div className="stack">
          <RequestStatusPath request={r} />
          <span className="row">
            <Badge kind={STATUS_KIND[r.status]}>{r.status_label}</Badge>
            <Badge kind={PRIORITY_KIND[r.priority]}>{PRIORITY_LABEL[r.priority]}</Badge>
            {r.story && <span className="faint">Story: {r.story.title}</span>}
            {r.for_project && <span className="faint">· {r.for_project.name}</span>}
          </span>
          <p className="faint">
            Requested {formatDate(r.created_at)}
            {r.requester ? ` by ${r.requester.name}` : ""}
          </p>
          <Facts request={r} />
          <Block title="Description" text={r.description} />
          <Block title="Why it matters" text={r.business_value} />
          <RequestFiles project={project} request={r} />
          <Comments
            api={api}
            base={`/projects/${project.id}/comments`}
            featureRequestId={r.id}
            onUnread={() => void qc.invalidateQueries({ queryKey: ["projects", project.id, "comments"] })}
          />
        </div>
      )}
    </Modal>
  );
}

function Facts({ request: r }: { request: FeatureRequest }) {
  const hours = hoursText(r.estimate_hours);
  return (
    <ul className="plain" aria-label="Where it stands">
      <li>
        <strong>Estimate:</strong> {hours ?? "Not estimated yet"}
      </li>
      {r.planned_in && (
        <li>
          <strong>Planned in:</strong> quote {r.planned_in}
        </li>
      )}
      {r.converted_story && (
        <li>
          <strong>Became the story:</strong> {r.converted_story.title}
        </li>
      )}
    </ul>
  );
}

function RequestFiles({ project, request }: { project: Project; request: FeatureRequest }) {
  const files = useFeatureRequestFiles(project.id, request.id);
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const upload = async (file: File) => {
    setBusy(true);
    try {
      await api.uploadFile(`/feature-requests/${request.id}/files`, file);
      toast(`${file.name} attached`);
      await qc.invalidateQueries({ queryKey: [...keys.featureRequest(project.id, request.id), "files"] });
    } catch (e) {
      toast(errorMessage(e), "err");
    } finally {
      setBusy(false);
    }
  };
  const list = files.data ?? [];
  return (
    <div className="stack">
      {list.length > 0 && (
        <ul className="plain" aria-label="Files">
          {list.map((f) => (
            <li key={f.id}>
              <a className="lk" href={fileUrl(`/projects/${project.id}/files/${f.id}`)} download={f.filename}>
                {f.filename}
              </a>{" "}
              <span className="faint">{fileSize(f.size)}</span>
            </li>
          ))}
        </ul>
      )}
      {project.can_log_feature_requests && (
        <label className="btn sm" aria-busy={busy}>
          Attach a sketch or file
          <input
            type="file"
            hidden
            aria-label="Attach a file"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) void upload(f);
            }}
          />
        </label>
      )}
    </div>
  );
}
